/**
 * BOSS Görev Yönetim Sayfası (resmi şema).
 * Sekme: Aktif / Arşiv. Görev oluştur / düzenle / arşivle / sil.
 * Teslim 3 — her kartta: hedeflenen kişiler + tek bakışta renkli durum,
 *            "Takip" → tarih seçici + detay + devam eden onay.
 * Veri (personel/şube/pozisyon/vardiya/tamamlama) bir kez çekilir,
 * her kart için yeniden kullanılır (performans).
 */

import { useEffect, useState, useMemo, useRef } from 'react'
import { Navigate, useSearchParams } from 'react-router-dom'
import {
  ClipboardList, Plus, Pencil, Archive, Trash2, RotateCcw, Clock, ListChecks,
  CheckCircle2, XCircle, Hourglass, Circle,
} from 'lucide-react'
import {
  tumGorevler, gorevArsivle, gorevAktiflestir, gorevSil,
  tumTamamlamalar, gorevGununDurumu, gorevKartTarihi, haftaBasiHesapla, oncekiGun,
  type GorevDoc,
} from '@/lib/services/gorev-service'
import { tumPersonelleriGetir } from '@/lib/services/personel-service'
import { tumSubeleriGetir } from '@/lib/services/sube-service'
import { tumPozisyonlariGetir } from '@/lib/services/pozisyon-service'
import { haftaVardiyalariGetir, type Vardiya } from '@/lib/services/vardiya-service'
import { useAuth } from '@/contexts/AuthContext'
import { useYetki } from '@/hooks/useYetki'
import { YETKILER } from '@/lib/yetkiler'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { GorevModal } from '@/components/gorev/GorevModal'
import { GorevTakipModal } from '@/components/gorev/GorevTakipModal'
import type {
  GorevTipi, Sube, Pozisyon, KullaniciPersonel, GorevTamamlamasi,
} from '@/types'

type Sekme = 'aktif' | 'arsiv'

const TIP_ETIKET: Record<GorevTipi, string> = {
  tekSeferlik: 'Tek seferlik',
  rutinGunluk: 'Her gün',
  rutinHaftalik: 'Her hafta',
  rutinAylik: 'Her ay',
  devamEden: 'Devam eden',
}

const HAFTA_ADI = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi']

export function GorevlerPage() {
  const { firebaseUser, kullanici } = useAuth()
  const gorebilir = useYetki(YETKILER.GOREV_GOR)
  const atayabilir = useYetki(YETKILER.GOREV_ATA)
  const isBoss = kullanici?.rol === 'boss'
  // Audit/log için işlemi yapanın rolü. BOSS değilse yönetici (sorumlu kalıbı).
  const rol: 'boss' | 'sorumlu' = isBoss ? 'boss' : 'sorumlu'

  const [gorevler, setGorevler] = useState<GorevDoc[]>([])
  const [subeler, setSubeler] = useState<Sube[]>([])
  const [pozisyonlar, setPozisyonlar] = useState<Pozisyon[]>([])
  const [personeller, setPersoneller] = useState<KullaniciPersonel[]>([])
  const [tamamlamalar, setTamamlamalar] = useState<GorevTamamlamasi[]>([])
  const [vardiyalar, setVardiyalar] = useState<Vardiya[]>([])
  const [yukleniyor, setYukleniyor] = useState(true)
  const [hata, setHata] = useState<string | null>(null)
  const [sekme, setSekme] = useState<Sekme>('aktif')

  const [modalAcik, setModalAcik] = useState(false)
  const [duzenlenen, setDuzenlenen] = useState<GorevDoc | null>(null)
  const [silinecek, setSilinecek] = useState<GorevDoc | null>(null)
  const [takipEdilen, setTakipEdilen] = useState<GorevDoc | null>(null)

  // Bildirimden gelince ?gorev=ID → o kart kırmızı yanıp söner.
  // sessionStorage'da tutulur → başka sayfaya gidip gelince DEVAM eder.
  // SADECE "Takip"e basınca (veya görev tamamlanınca) durur.
  const VURGU_KEY = 'gorevVurguId'
  const [searchParams, setSearchParams] = useSearchParams()
  const [vurgulananId, setVurgulananIdState] = useState<string | null>(
    () => sessionStorage.getItem(VURGU_KEY)
  )
  const [yanipSonuyor, setYanipSonuyor] = useState<boolean>(
    () => !!sessionStorage.getItem(VURGU_KEY)
  )
  const vurguRef = useRef<HTMLDivElement | null>(null)

  function vurguAyarla(id: string) {
    sessionStorage.setItem(VURGU_KEY, id)
    setVurgulananIdState(id)
    setYanipSonuyor(true)
  }

  // URL'de ?gorev=ID gelince sessionStorage'a al, URL'yi temizle.
  useEffect(() => {
    const id = searchParams.get('gorev')
    if (id) {
      vurguAyarla(id)
      searchParams.delete('gorev')
      setSearchParams(searchParams, { replace: true })
    }
  }, [searchParams, setSearchParams])

  // Veri yüklenince vurgulanan karta kaydır.
  useEffect(() => {
    if (vurgulananId && yanipSonuyor) {
      const t = setTimeout(() => {
        vurguRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }, 300)
      return () => clearTimeout(t)
    }
  }, [vurgulananId, yanipSonuyor, gorevler])

  function vurguyuDurdur() {
    sessionStorage.removeItem(VURGU_KEY)
    setYanipSonuyor(false)
    setVurgulananIdState(null)
  }

  async function yukle() {
    try {
      setYukleniyor(true)
      const [g, p, s, poz, t] = await Promise.all([
        tumGorevler(),
        tumPersonelleriGetir(),
        tumSubeleriGetir(),
        tumPozisyonlariGetir(),
        tumTamamlamalar(),
      ])
      setGorevler(g)
      setPersoneller(p)
      setSubeler(s)
      setPozisyonlar(poz)
      setTamamlamalar(t)

      // Saat aralığı + mesaiDisiDahil=false olan görevler için vardiya gerekir.
      // İhtiyaç duyulan hafta(lar)ı topla, şube başına bir kez çek.
      const haftaSet = new Set<string>()
      for (const gr of g) {
        if (gr.durum !== 'aktif') continue
        if (!gr.zamanAraligi || gr.mesaiDisiDahil) continue
        const kt = gorevKartTarihi(gr)
        haftaSet.add(haftaBasiHesapla(kt))
        // Dünden sarkan gece vardiyaları farklı haftada olabilir (Pzt sabahı) — onu da çek.
        haftaSet.add(haftaBasiHesapla(oncekiGun(kt)))
      }
      const vAll: Vardiya[] = []
      for (const hafta of haftaSet) {
        for (const sb of s) {
          const v = await haftaVardiyalariGetir(hafta, sb.id)
          vAll.push(...v)
        }
      }
      setVardiyalar(vAll)
      setHata(null)
    } catch (err) {
      console.error(err)
      setHata('Yükleme hatası: ' + (err as Error).message)
    } finally {
      setYukleniyor(false)
    }
  }

  useEffect(() => {
    if (gorebilir) yukle()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gorebilir])

  const personelHaritasi = useMemo(() => {
    const m = new Map<string, KullaniciPersonel>()
    personeller.forEach((p) => m.set(p.id, p))
    return m
  }, [personeller])

  const subeHaritasi = useMemo(() => {
    const m = new Map<string, Sube>()
    subeler.forEach((s) => m.set(s.id, s))
    return m
  }, [subeler])

  const pozHaritasi = useMemo(() => {
    const m = new Map<string, Pozisyon>()
    pozisyonlar.forEach((p) => m.set(p.id, p))
    return m
  }, [pozisyonlar])

  const sayilar = useMemo(() => ({
    aktif: gorevler.filter((g) => g.durum === 'aktif').length,
    arsiv: gorevler.filter((g) => g.durum !== 'aktif').length,
  }), [gorevler])

  const filtreli = useMemo(
    () => gorevler.filter((g) => (sekme === 'aktif' ? g.durum === 'aktif' : g.durum !== 'aktif')),
    [gorevler, sekme]
  )

  if (!gorebilir) return <Navigate to="/yetkisiz" replace />

  function hedefOzeti(g: GorevDoc): string {
    const p: string[] = []
    if (g.hedef?.subeIdleri?.length) {
      p.push(g.hedef.subeIdleri.map((id) => subeHaritasi.get(id)?.isim ?? 'Şube').join(', '))
    }
    if (g.hedef?.pozisyonIdleri?.length) {
      p.push(g.hedef.pozisyonIdleri.map((id) => pozHaritasi.get(id)?.isim ?? 'Pozisyon').join(', '))
    }
    if (g.hedef?.personelIdleri?.length) {
      p.push(g.hedef.personelIdleri.map((id) => personelHaritasi.get(id)?.isim ?? 'Personel').join(', '))
    }
    return p.length ? p.join(' · ') : '—'
  }

  function zamanlamaOzeti(g: GorevDoc): string {
    const bt = g.baslangicTarihi as unknown as string | undefined
    if (!bt || !bt.includes('-')) return 'Eski biçim kayıt — silinmeli'
    const [y, m, d] = bt.split('-').map(Number)
    const dt = new Date(y, m - 1, d)
    let s = ''
    switch (g.tipi) {
      case 'tekSeferlik':
        s = dt.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })
        break
      case 'rutinGunluk': s = 'Her gün'; break
      case 'rutinHaftalik': s = `Her ${HAFTA_ADI[dt.getDay()]}`; break
      case 'rutinAylik': s = `Her ayın ${d}'i`; break
      case 'devamEden': s = 'Bitene kadar'; break
    }
    if (g.zamanAraligi) s += ` · ${g.zamanAraligi.baslangic}–${g.zamanAraligi.bitis}`
    return s
  }

  async function arsivIslem(g: GorevDoc) {
    if (!firebaseUser) return
    try {
      if (g.durum === 'aktif') {
        await gorevArsivle(g.id, firebaseUser.uid, kullanici?.isim, rol)
      } else {
        await gorevAktiflestir(g.id)
      }
      await yukle()
    } catch (err) {
      alert('Hata: ' + (err as Error).message)
    }
  }

  async function silOnay() {
    if (!silinecek || !firebaseUser) return
    try {
      await gorevSil(silinecek.id, firebaseUser.uid, kullanici?.isim, rol)
      setSilinecek(null)
      await yukle()
    } catch (err) {
      alert('Hata: ' + (err as Error).message)
      setSilinecek(null)
    }
  }

  return (
    <div>
      <style>{`
        @keyframes gorevVurguYanip {
          0%, 100% { box-shadow: 0 0 0 0 rgba(239,68,68,0); border-color: rgb(239,68,68); }
          50% { box-shadow: 0 0 0 6px rgba(239,68,68,0.45); border-color: rgb(185,28,28); }
        }
        .gorev-vurgu-yanip {
          animation: gorevVurguYanip 0.9s ease-in-out infinite;
          border-color: rgb(239,68,68) !important;
        }
      `}</style>
      <div className="flex items-center justify-between gap-2 mb-6">
        <div className="flex items-center gap-2">
          <ClipboardList className="w-6 h-6 text-brand-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Görevler</h1>
            <p className="text-sm text-gray-600 mt-0.5">{sayilar.aktif} aktif görev</p>
          </div>
        </div>
        {atayabilir && (
          <button
            onClick={() => { setDuzenlenen(null); setModalAcik(true) }}
            className="btn-primary text-sm py-2 px-4 flex items-center gap-1.5 whitespace-nowrap"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Yeni Görev</span>
          </button>
        )}
      </div>

      <div className="flex gap-1.5 mb-4 bg-gray-100 p-1 rounded-xl">
        <TabBtn aktif={sekme === 'aktif'} onClick={() => setSekme('aktif')} etiket="Aktif" sayi={sayilar.aktif} />
        <TabBtn aktif={sekme === 'arsiv'} onClick={() => setSekme('arsiv')} etiket="Arşiv" sayi={sayilar.arsiv} />
      </div>

      {hata && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-3 text-sm mb-4">{hata}</div>
      )}

      {yukleniyor ? (
        <div className="card text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
        </div>
      ) : filtreli.length === 0 ? (
        <div className="card text-center py-12">
          <ClipboardList className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="font-semibold text-gray-700">
            {sekme === 'aktif' ? 'Aktif görev yok' : 'Arşivde görev yok'}
          </p>
          {sekme === 'aktif' && atayabilir && (
            <button
              onClick={() => { setDuzenlenen(null); setModalAcik(true) }}
              className="btn-primary text-sm py-2 px-4 mt-4 inline-flex items-center gap-1.5"
            >
              <Plus className="w-4 h-4" /> İlk görevi oluştur
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {filtreli.map((g) => {
            const tarih = gorevKartTarihi(g)
            const kisiler = gorevGununDurumu(g, tarih, personeller, vardiyalar, tamamlamalar)
            const tamamSay = kisiler.filter((k) => k.durum === 'tamamlandi').length
            const yapilamadiSay = kisiler.filter((k) => k.durum === 'yapilamadi').length
            const redSay = kisiler.filter((k) => k.durum === 'reddedildi').length
            const acikSay = kisiler.filter((k) => k.durum === 'bekliyor').length
            const hepsiTamam = kisiler.length > 0 && tamamSay === kisiler.length
            const sorunVar = yapilamadiSay > 0 || redSay > 0
                || (g.durum === 'aktif' && acikSay > 0)
            // 3 belirgin durum (maliyet ekranı gibi: koyu ton + kalın sol şerit)
            //  yeşil = herkes tamam · turuncu = kısmen · kırmızı = sorunlu
            const renk = hepsiTamam
              ? { kenar: 'border-green-500', bg: 'bg-green-100', serit: 'border-l-green-600' }
              : sorunVar
                ? { kenar: 'border-red-500', bg: 'bg-red-100', serit: 'border-l-red-600' }
                : { kenar: 'border-amber-500', bg: 'bg-amber-100', serit: 'border-l-amber-600' }
            const buKartVurgulu = vurgulananId === g.id
            const yanip = buKartVurgulu && yanipSonuyor

            return (
              <div
                key={g.id}
                ref={buKartVurgulu ? vurguRef : null}
                className={`card border-2 border-l-8 ${renk.kenar} ${renk.serit} ${renk.bg} ${
                  yanip ? 'gorev-vurgu-yanip' : buKartVurgulu ? 'ring-2 ring-red-500' : ''
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <h3 className="font-bold text-gray-900">{g.baslik}</h3>
                      <span className="text-xs font-semibold bg-brand-50 text-brand-700 px-2 py-0.5 rounded-full">
                        {TIP_ETIKET[g.tipi] ?? 'Eski kayıt'}
                      </span>
                      {g.durum === 'kapandi' && (
                        <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full">kapandı</span>
                      )}
                      {g.aciklamaZorunlu && (
                        <span className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-full">açıklama zorunlu</span>
                      )}
                      {g.fotoZorunlu && (
                        <span className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded-full">foto zorunlu</span>
                      )}
                      {g.zamanAraligi && !g.mesaiDisiDahil && (
                        <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">sadece mesaidekiler</span>
                      )}
                    </div>
                    <p className="text-sm text-gray-700">{g.aciklama}</p>
                    <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {zamanlamaOzeti(g)}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">Kime: {hedefOzeti(g)}</p>

                    {/* Kişi durumları — tek bakışta */}
                    {kisiler.length > 0 ? (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {kisiler.map(({ personel, durum }) => (
                          <span
                            key={personel.id}
                            className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full border ${
                              durum === 'tamamlandi' ? 'bg-green-50 text-green-700 border-green-200'
                              : durum === 'yapilamadi' ? 'bg-red-50 text-red-700 border-red-200'
                              : durum === 'reddedildi' ? 'bg-orange-50 text-orange-700 border-orange-300'
                              : durum === 'beklemede' ? 'bg-blue-50 text-blue-700 border-blue-200'
                              : 'bg-gray-50 text-gray-500 border-gray-200'
                            }`}
                          >
                            {durum === 'tamamlandi' && <CheckCircle2 className="w-3 h-3" />}
                            {durum === 'yapilamadi' && <XCircle className="w-3 h-3" />}
                            {durum === 'reddedildi' && <XCircle className="w-3 h-3" />}
                            {durum === 'beklemede' && <Hourglass className="w-3 h-3" />}
                            {durum === 'bekliyor' && <Circle className="w-3 h-3" />}
                            {personel.isim}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="text-[11px] text-gray-400 mt-2">
                        Bugün için kimseyi kapsamıyor.
                      </p>
                    )}
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <button
                      onClick={() => {
                        vurguyuDurdur()
                        setTakipEdilen(g)
                      }}
                      className="text-xs text-brand-700 hover:text-brand-800 border border-brand-200 hover:border-brand-300 bg-brand-50 py-1.5 px-2 rounded-lg flex items-center gap-1 whitespace-nowrap"
                    >
                      <ListChecks className="w-3 h-3" /> Takip
                    </button>
                    {atayabilir && g.durum === 'aktif' && (
                      <button
                        onClick={() => { setDuzenlenen(g); setModalAcik(true) }}
                        className="text-xs text-gray-600 hover:text-gray-900 border border-gray-300 hover:border-gray-400 py-1.5 px-2 rounded-lg flex items-center gap-1 whitespace-nowrap"
                      >
                        <Pencil className="w-3 h-3" /> Düzenle
                      </button>
                    )}
                    {atayabilir && (
                      <button
                        onClick={() => arsivIslem(g)}
                        className="text-xs text-gray-600 hover:text-gray-900 border border-gray-300 hover:border-gray-400 py-1.5 px-2 rounded-lg flex items-center gap-1 whitespace-nowrap"
                      >
                        {g.durum === 'aktif'
                          ? <><Archive className="w-3 h-3" /> Arşivle</>
                          : <><RotateCcw className="w-3 h-3" /> Geri al</>}
                      </button>
                    )}
                    {isBoss && (
                      <button
                        onClick={() => setSilinecek(g)}
                        className="text-xs text-red-600 hover:text-red-800 border border-red-200 hover:border-red-300 py-1.5 px-2 rounded-lg flex items-center gap-1 whitespace-nowrap"
                      >
                        <Trash2 className="w-3 h-3" /> Sil
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      <GorevModal
        acik={modalAcik}
        gorev={duzenlenen}
        subeler={subeler}
        pozisyonlar={pozisyonlar}
        personeller={personeller}
        onKapat={() => setModalAcik(false)}
        onKaydedildi={() => { setModalAcik(false); yukle() }}
      />

      <GorevTakipModal
        acik={!!takipEdilen}
        gorev={takipEdilen}
        personeller={personeller}
        subeler={subeler}
        tumTamamlama={tamamlamalar}
        onaylayanId={firebaseUser?.uid ?? ''}
        onKapat={() => setTakipEdilen(null)}
        onDegisti={yukle}
      />

      <ConfirmDialog
        acik={!!silinecek}
        baslik="Görevi Sil"
        mesaj={`"${silinecek?.baslik ?? ''}" görevini kalıcı olarak silmek istiyor musun? Bu işlem geri alınamaz. (Saklamak istersen "Arşivle"yi kullan.)`}
        onayMetni="Kalıcı Sil"
        onOnay={silOnay}
        onIptal={() => setSilinecek(null)}
      />
    </div>
  )
}

interface TabBtnProps {
  aktif: boolean
  onClick: () => void
  etiket: string
  sayi: number
}

function TabBtn({ aktif, onClick, etiket, sayi }: TabBtnProps) {
  return (
    <button
      onClick={onClick}
      className={`flex-1 px-3 py-2 rounded-lg text-sm font-semibold flex items-center justify-center gap-1.5 transition-colors ${
        aktif ? 'bg-white text-brand-700 shadow-sm' : 'text-gray-600 hover:bg-gray-200'
      }`}
    >
      <span>{etiket}</span>
      {sayi > 0 && (
        <span
          className={`px-1.5 py-0.5 rounded-full text-xs ${
            aktif ? 'bg-brand-100 text-brand-700' : 'bg-gray-300 text-gray-700'
          }`}
        >
          {sayi}
        </span>
      )}
    </button>
  )
}
